//frontend/src/auth/RoleRoute.tsx
import React from 'react';
import type { ReactElement } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useLocation, Navigate } from 'react-router-dom';

/**
 * Checks a custom claim set by your Post-Login Action or a user app_metadata.
 * For now we’ll use a token claim:
 *   https://mitoflux.is/role = 'doctor' | 'pharmacist'
 *
 * If you’re not setting a role claim yet, you can temporarily check user.email allowlist,
 * but best is to set a role claim once Signicat is wired.
 */
export default function RoleRoute({
  children,
  allowedRoles,
}: {
  children: ReactElement;
  allowedRoles: string[];
}) {
  const { isAuthenticated, isLoading, loginWithRedirect, user } = useAuth0();
  const location = useLocation();

  if (isLoading) return <div>Loading…</div>;

  // If Auth0 redirected back with an error, don’t loop
  const params = new URLSearchParams(location.search);
  if (params.get('error') === 'access_denied') {
    return <Navigate to={`/access-denied${location.search}`} replace />;
  }

  if (!isAuthenticated) {
    loginWithRedirect({
      appState: { returnTo: location.pathname + location.search },
    });
    return <div>Redirecting to login…</div>;
  }

  const role = (user as any)?.['https://mitoflux.is/role'];

  // don’t redirect until we actually have the claim
  if (!role) return <div>Loading user role…</div>;

  if (!role || !allowedRoles.includes(role)) {
    return <Navigate to="/forbidden" replace />;
  }

  return children;
}
